import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
  UseMutationOptions,
  UseMutationResult,
  useMutation,
} from 'react-query';
import { axiosClient, url } from '@/api';
import { AxiosError } from 'axios';
import { ErrorObject, IList, SearchPaginationFilterParams } from '../common.types';

export enum ReferralStatus {
  SIGNED_UP = 'SIGNED_UP',
  TRADED = 'TRADED',
}
export interface IUserReferralDetail {
  earnedFromEmail?: string;
  currentRate?: number;
  signedUpDateAndTime?: string;
  referralStatus?: ReferralStatus;
}

export function useGetCodes<TSelectData = { code: string; name: string }, TError = AxiosError>(
  options?: Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined,
): UseQueryResult<TSelectData, TError> {
  return useQuery<void, TError, TSelectData>(
    ['GetCodes'],
    () => axiosClient.post(url.referralController.getCodes),
    options,
  );
}

export function useSendReferralLinkToEmail<TSelectData = void, TError = AxiosError>(
  options: [{ email: string }, Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined],
): UseQueryResult<TSelectData, TError> {
  return useQuery<void, TError, TSelectData>(
    ['SendReferralLinkToEmail'],
    () => axiosClient.get(url.referralController.sendToEmail(options[0].email)),
    options?.[1],
  );
}

export function useGetList<TSelectData = IList<IUserReferralDetail>, TError = AxiosError>(
  options: [
    SearchPaginationFilterParams,
    Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined,
  ],
): UseQueryResult<TSelectData, TError> {
  const queryKeys = options[0];
  return useQuery<void, TError, TSelectData>(
    ['GetList', ...Object.values(queryKeys)],
    () =>
      axiosClient.get(
        url.referralController.getList({
          page: options[0]?.page ?? 0,
          size: options[0]?.size ?? 8,
          sortField: options[0]?.sortField ?? 'created',
          isDesc: options[0]?.isDesc ?? true,
        }),
      ),
    options[1],
  );
}

export function useGetTotal<
  TSelectData = {
    totalEarningsAmount: number;
    availableToCashOut: number;
  },
  TError = AxiosError,
>(
  options?: Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined,
): UseQueryResult<TSelectData, TError> {
  return useQuery<void, TError, TSelectData>(
    ['GetTotal'],
    () => axiosClient.get(url.referralController.getTotal),
    options,
  );
}

interface useCashoutResponse {
  id: string;
  audAmount: number;
  fiatAmount: number;
  currencyToCashOut: 'USD';
  payoutType: string;
  bsb: string;
  accountNo: string;
  accountName: string;
  bic: string;
  iban: string;
  cnaps: string;
  sortCode: string;
  ifsc: string;
  clabe: string;
  aba: string;
  payoutCountryCode: string;
  billerCode: string;
  billerReference: string;
  description: string;
}

interface useCashoutPayload {
  audAmount: number;
  bsb: string;
  accountNo: string;
  description?: 'Referral cashout';
}

export function useCashout<TContext>(
  options?: Omit<
    UseMutationOptions<useCashoutResponse, AxiosError<ErrorObject>, useCashoutPayload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<useCashoutResponse, AxiosError<ErrorObject>, useCashoutPayload, TContext> {
  const key = ['Cashout'];

  return useMutation((body: useCashoutPayload) => axiosClient.post(url.referralController.cashout, { ...body }), {
    ...options,
    mutationKey: key,
  });
}
